import { MailOpen } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router";
import { useAuth } from "../../auth/AuthProvider";
import { Button, ButtonLink } from "../../components/Button";
import { FormAlert } from "../../components/fields";
import { useDocumentTitle } from "../../components/layout";
import { authErrorMessage } from "../../lib/firebase";
import { safeNext, takeNext } from "../guards";
import { AuthLayout } from "./AuthLayout";
import { sendVerificationEmail, verificationErrorMessage } from "./Register";

const RESEND_COOLDOWN = 30;

function initialMessage(sent: string | null): { tone: "error" | "info" | "success"; text: string } | null {
  if (sent === "1") return { tone: "success", text: "Verification email sent." };
  if (sent === "0") return { tone: "error", text: "We couldn't send the verification email just now. Try again below." };
  return null;
}

export function VerifyEmailPage() {
  useDocumentTitle("Verify your email");
  const { user, config, refreshVerification, signOut } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const nextParam = params.get("next");
  const [checking, setChecking] = useState(false);
  const [resending, setResending] = useState(false);
  const [cooldown, setCooldown] = useState(params.get("sent") === "1" ? RESEND_COOLDOWN : 0);
  const [message, setMessage] = useState<{ tone: "error" | "info" | "success"; text: string } | null>(initialMessage(params.get("sent")));

  useEffect(() => {
    if (cooldown <= 0) return;
    const id = window.setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => window.clearTimeout(id);
  }, [cooldown]);

  const check = useCallback(
    async (manual: boolean) => {
      setChecking(manual);
      try {
        const ok = await refreshVerification();
        if (ok) navigate(safeNext(nextParam, takeNext()), { replace: true });
        else if (manual) setMessage({ tone: "info", text: "Not verified yet. Open the link in the email, then try again." });
      } catch (error) {
        if (manual) setMessage({ tone: "error", text: authErrorMessage(error) });
      } finally {
        setChecking(false);
      }
    },
    [navigate, refreshVerification, nextParam],
  );

  // Coming back to this tab after clicking the link elsewhere: check automatically.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible") void check(false);
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
    };
  }, [check]);

  if (!user) return <Navigate to="/login" replace />;
  if (user.emailVerified) return <Navigate to={safeNext(nextParam, takeNext())} replace />;

  return (
    <AuthLayout title="Verify your email" footer={<Button emphasis="ghost" intent="neutral" onClick={() => void signOut()}>Sign out</Button>}>
      <div className="auth-sent">
        <MailOpen size={40} aria-hidden="true" />
        <p>
          We sent a verification link to <strong>{user.email}</strong>. Open it to finish setting up your account.
        </p>
      </div>
      {message ? <FormAlert tone={message.tone}>{message.text}</FormAlert> : null}
      <div className="stack stack--sm">
        <Button emphasis="solid" intent="brand" size="lg" block busy={checking} onClick={() => void check(true)}>
          I've verified my email
        </Button>
        {config.devOutbox ? (
          <ButtonLink to="/dev/outbox" emphasis="outline" intent="info" block>
            Open the local outbox
          </ButtonLink>
        ) : null}
        <Button
          emphasis="outline"
          intent="neutral"
          block
          busy={resending}
          disabled={cooldown > 0}
          onClick={async () => {
            setResending(true);
            try {
              await sendVerificationEmail(user, config);
              setMessage({ tone: "success", text: "Sent another verification email." });
              setCooldown(RESEND_COOLDOWN);
            } catch (error) {
              // Never claim a send that failed; the button stays available.
              setMessage({ tone: "error", text: verificationErrorMessage(error) });
            } finally {
              setResending(false);
            }
          }}
        >
          {cooldown > 0 ? `Resend email (${cooldown}s)` : "Resend email"}
        </Button>
      </div>
    </AuthLayout>
  );
}
